import React, { useContext, useState } from 'react';
import { Box, Avatar, Menu, MenuItem, ListItemIcon, Typography, Divider, Tooltip } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import Roles from '../config/roles';
import { useTranslation } from 'react-i18next';

// Avatar + dropdown shown in the top bar (next to the notification bell). Links to
// the dedicated account pages and holds Log out. Company + Subscription are admin-only.
const AccountMenu = () => {
    const { t } = useTranslation();
    const { role, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const firstName = localStorage.getItem('FIRST_NAME') || '';

    const open = Boolean(anchorEl);
    const close = () => setAnchorEl(null);
    const go = (path) => { close(); navigate(path); };
    const handleLogout = () => {
        close();
        localStorage.removeItem('FIRST_NAME');
        logout();
    };

    const items = [
        { label: t('settings.tabs.profile', { defaultValue: 'Profile' }), icon: <PersonOutlineIcon fontSize="small" />, path: '/account/profile' },
        ...(role === Roles.ADMIN ? [
            { label: t('settings.tabs.company', { defaultValue: 'Company details' }), icon: <BusinessOutlinedIcon fontSize="small" />, path: '/account/company' },
            { label: t('settings.tabs.subscription', { defaultValue: 'Subscription' }), icon: <CreditCardOutlinedIcon fontSize="small" />, path: '/account/subscription' },
        ] : []),
    ];

    return (
        <>
            <Tooltip title={firstName || t('nav.account', { defaultValue: 'Account' })}>
                <Box
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                    <Avatar sx={{
                        width: 34, height: 34, fontSize: 14, fontWeight: 700, color: '#fff',
                        background: 'linear-gradient(135deg, #4e90b5 0%, #427898 100%)',
                        transition: 'filter 0.15s',
                        '&:hover': { filter: 'brightness(1.06)' },
                    }}>
                        {firstName?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                </Box>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 210, borderRadius: 2, mt: 1 } } }}
            >
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1a2332', lineHeight: 1.3 }}>
                        {firstName || t('nav.account', { defaultValue: 'Account' })}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                        {role === Roles.ADMIN ? 'Administrator' : 'User'}
                    </Typography>
                </Box>
                <Divider />

                {items.map(it => (
                    <MenuItem key={it.path} onClick={() => go(it.path)} sx={{ fontSize: 13.5 }}>
                        <ListItemIcon sx={{ color: '#427898' }}>{it.icon}</ListItemIcon>
                        {it.label}
                    </MenuItem>
                ))}

                <Divider />
                <MenuItem onClick={handleLogout} sx={{ fontSize: 13.5 }}>
                    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                    {t('nav.logOut', { defaultValue: 'Log out' })}
                </MenuItem>
            </Menu>
        </>
    );
};

export default AccountMenu;
